import { useEffect, useMemo, useRef, useState } from 'react';
import {
  canEditImpression,
  fetchMineImpressions,
  fetchVenueImpressions,
  IMPRESSION_VERDICT_LABELS,
  type GuestImpression,
  type ImpressionVerdict,
} from './guestImpressionsApi';
import {
  type DataEnvelope,
  type GuestActivation,
  type GuestOffer,
  type GuestOfferPage,
  GuestOffersApiError,
  guestOffersFetch,
  offerRequirementText,
  offersPageEndpoint,
  offerStateLabel,
} from './guestOffersApi';
import { fetchEvent, fetchVenueEvents, type EventSummary } from './events/eventsApi';
import { VenueClaimModal } from './VenueClaimModal';
import { VenuePostsSection } from './posts/VenuePostsSection';
import {
  fetchRelatedVenuePlacement,
  recordRelatedVenuePlacementEvent,
  type RelatedVenuePlacement,
} from './relatedVenuePlacementApi';
import type { ProfileVisit } from './socialApi';
import { Button } from './ui/Button';
import { FriendAvatarsStack } from './ui/FriendAvatarsStack';
import { HopperIcon } from './ui/icons/HopperIcon';
import {
  fetchAllMyVisits,
  fetchCityVenues,
  fetchVenuePhotos,
  subscribeVenue,
  unsubscribeVenue,
  type CityVenue,
  type LegacyVenueOpeningHours,
  type VenueDetailData,
  type VenueOpeningHour,
  type VenuePhoto,
} from './venueDetailApi';
import type { VenueDetailCompactScreenProps } from './VenueDetailCompactScreen';
import './venue-detail-reference.css';
import './venue-detail-owner-v2.css';
import './venue-detail-offer-cta.css';

type OpeningRow = { day: string; value: string };
type VenueEvent = EventSummary & { description?: string | null };
type Workspace = 'overview' | 'posts' | 'offers' | 'events' | 'photos' | 'impressions' | 'visits';
type ScoreHelp = 'hopper' | 'live' | 'metrics' | null;
type PhotoFilter = 'all' | 'official' | 'guest';

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const LEGACY_DAYS: Array<keyof LegacyVenueOpeningHours> = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];
const RELATED_LIMIT = 6;

const WORKSPACE_TABS: Array<{ id: Workspace; label: string }> = [
  { id: 'overview', label: 'О заведении' },
  { id: 'posts', label: 'Посты' },
  { id: 'offers', label: 'Предложения' },
  { id: 'events', label: 'События' },
  { id: 'photos', label: 'Фото' },
  { id: 'impressions', label: 'Впечатления' },
  { id: 'visits', label: 'Мои визиты' },
];

const PHOTO_FILTERS: Array<{ id: PhotoFilter; label: string }> = [
  { id: 'all', label: 'Все' },
  { id: 'official', label: 'Официальные' },
  { id: 'guest', label: 'От гостей' },
];

function initialVenueWorkspace(): Workspace {
  return new URL(window.location.href).searchParams.get('tab') === 'photos' ? 'photos' : 'overview';
}

function replaceVenueWorkspaceQuery(workspace: Workspace): void {
  const url = new URL(window.location.href);
  if (workspace === 'photos') {
    url.searchParams.set('tab', 'photos');
  } else {
    url.searchParams.delete('tab');
  }

  window.history.replaceState(
    window.history.state ?? {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function pageViewId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function normalizeOpeningRows(hours: VenueDetailData['opening_hours']): OpeningRow[] | null {
  if (!hours) return null;

  if (Array.isArray(hours)) {
    const byDay = new Map<number, VenueOpeningHour>();
    hours.forEach((row) => {
      if (Number.isInteger(row.day) && row.day >= 0 && row.day <= 6) byDay.set(row.day, row);
    });
    if (byDay.size === 0) return null;
    return DAY_LABELS.map((day, index) => {
      const row = byDay.get(index);
      if (!row) return { day, value: 'Не указано' };
      if (row.is_closed) return { day, value: 'Закрыто' };
      if (!row.open || !row.close) return { day, value: 'Не указано' };
      return { day, value: `${row.open}–${row.close}` };
    });
  }

  const legacy = hours as LegacyVenueOpeningHours;
  if (!LEGACY_DAYS.some((day) => legacy[day] !== undefined)) return null;
  return LEGACY_DAYS.map((day, index) => {
    const row = legacy[day];
    if (!row) return { day: DAY_LABELS[index], value: 'Не указано' };
    if (row.is_closed === true) return { day: DAY_LABELS[index], value: 'Закрыто' };
    if (!row.open || !row.close) return { day: DAY_LABELS[index], value: 'Не указано' };
    return { day: DAY_LABELS[index], value: `${row.open}–${row.close}` };
  });
}

function openingStatusCopy(detail: VenueDetailData, rows: OpeningRow[] | null): string | null {
  if (!rows) return null;
  if (detail.opening_status === 'open') return 'Открыто';
  if (detail.opening_status === 'closed') return 'Закрыто';
  return null;
}

function shortDescription(value: string | null): string | null {
  const text = value?.trim();
  if (!text) return null;
  const firstLine = text.split(/\n+/)[0]?.trim() ?? text;
  return firstLine.length <= 116 ? firstLine : `${firstLine.slice(0, 113).trimEnd()}…`;
}

function shortDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

function eventDate(value: string | null, timezone: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: timezone ?? undefined,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(date);
  }
}

function formatLiveScore(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function verdictClass(verdict: ImpressionVerdict): string {
  return verdict === 'yes' ? 'is-positive' : verdict === 'maybe' ? 'is-neutral' : 'is-negative';
}

async function fetchAllVenuePhotos(venueId: string, signal: AbortSignal): Promise<VenuePhoto[]> {
  const items: VenuePhoto[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;

  do {
    const response = await fetchVenuePhotos(venueId, cursor, 50, signal);
    response.data.forEach((photo) => {
      if (!seen.has(photo.id)) {
        seen.add(photo.id);
        items.push(photo);
      }
    });
    cursor = response.meta.has_more ? response.meta.next_cursor : null;
  } while (cursor && !signal.aborted);

  return items;
}

async function fetchAllVenueOffers(detail: VenueDetailData, venueId: string, signal: AbortSignal): Promise<GuestOffer[]> {
  const context = { mode: 'venue' as const, city: detail.city.slug, venueId };
  const items: GuestOffer[] = [];
  const seen = new Set<string>();
  let page = 1;

  for (;;) {
    const response = await guestOffersFetch<GuestOfferPage>(offersPageEndpoint(context, 'all', page), { signal });
    response.data.forEach((offer) => {
      if (!seen.has(offer.id)) {
        seen.add(offer.id);
        items.push(offer);
      }
    });
    if (!response.meta.has_more || signal.aborted) break;
    page = response.meta.current_page + 1;
  }

  return items;
}

async function fetchAllVenueEvents(venueId: string, signal: AbortSignal): Promise<VenueEvent[]> {
  const summaries: EventSummary[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;

  do {
    const response = await fetchVenueEvents(venueId, cursor, 20, signal);
    response.data.forEach((event) => {
      if (!seen.has(event.id)) {
        seen.add(event.id);
        summaries.push(event);
      }
    });
    cursor = response.meta.has_more ? response.meta.next_cursor : null;
  } while (cursor && !signal.aborted);

  return Promise.all(summaries.map(async (event) => {
    try {
      const response = await fetchEvent(event.id);
      return { ...event, description: response.event.description };
    } catch {
      return event;
    }
  }));
}

async function fetchAllVenueImpressions(venueId: string, routeReferences: string[]): Promise<GuestImpression[]> {
  const items: GuestImpression[] = [];
  const seen = new Set<string>();
  let page = 1;

  for (;;) {
    const response = await fetchVenueImpressions(venueId, page, routeReferences);
    response.data.forEach((impression) => {
      if (!seen.has(impression.id)) {
        seen.add(impression.id);
        items.push(impression);
      }
    });
    if (!response.links?.next) break;
    page += 1;
  }

  return items;
}

async function fetchAllMineImpressions(): Promise<GuestImpression[]> {
  const items: GuestImpression[] = [];
  const seen = new Set<string>();
  let page = 1;

  for (;;) {
    const response = await fetchMineImpressions(page);
    response.data.forEach((impression) => {
      if (!seen.has(impression.id)) {
        seen.add(impression.id);
        items.push(impression);
      }
    });
    if (!response.links?.next) break;
    page += 1;
  }

  return items;
}

async function fetchRelatedVenues(detail: VenueDetailData, venueId: string, signal: AbortSignal): Promise<CityVenue[]> {
  const items: CityVenue[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;

  do {
    const response = await fetchCityVenues(detail.city.slug || detail.city.id, RELATED_LIMIT + 1, cursor, signal);
    response.items.forEach((venue) => {
      if (items.length >= RELATED_LIMIT) return;
      if (venue.id !== venueId && !seen.has(venue.id)) {
        seen.add(venue.id);
        items.push(venue);
      }
    });
    cursor = response.pagination.has_more && items.length < RELATED_LIMIT ? response.pagination.next_cursor : null;
  } while (cursor && !signal.aborted);

  return items;
}

function VenueAvatar({ name, url, className = '' }: { name: string | null; url: string | null; className?: string }) {
  if (url) return <img className={`venue-owner-avatar ${className}`.trim()} src={url} alt="" />;
  return (
    <span
      className={`venue-owner-avatar venue-owner-avatar--placeholder ${className}`.trim()}
      role="img"
      aria-label={name?.trim() ? `Аватар ${name} не задан` : 'Аватар заведения не задан'}
    >
      <HopperIcon name="building" size={22} />
    </span>
  );
}

function ScoreHelpPanel({ mode, detail }: { mode: Exclude<ScoreHelp, null>; detail: VenueDetailData }) {
  const score = detail.scores;
  if (!score) return null;

  if (mode === 'hopper') {
    return (
      <div className="venue-owner-score-help" role="status">
        <strong>Hopper Score</strong>
        <p>Долгосрочная репутация заведения в Хоппере. Она строится на подтверждённых визитах и устойчивой активности, а не на разовом всплеске.</p>
      </div>
    );
  }

  if (mode === 'live') {
    return (
      <div className="venue-owner-score-help" role="status">
        <strong>Live Score</strong>
        <p>Показывает свежую активность вокруг места: насколько активно сюда приходят подтверждённые гости сейчас и в последние недели.</p>
      </div>
    );
  }

  return (
    <div className="venue-owner-score-help venue-owner-score-help--metrics" role="status">
      <strong>Динамика Live Score</strong>
      <div><span>Сегодня <b>{score.live_score_today}</b></span><span>7 дней <b>{score.live_score_7d}</b></span><span>30 дней <b>{score.live_score_30d}</b></span></div>
      <p>За 7 дней: {score.live_unique_guests_7d.toLocaleString('ru-RU')} уникальных гостей и {score.live_unique_visits_7d.toLocaleString('ru-RU')} подтверждённых визитов.</p>
    </div>
  );
}

type VenueInfoCardProps = {
  detail: VenueDetailData;
  openingRows: OpeningRow[] | null;
  statusCopy: string | null;
  statusClass: string;
  compactDescription: string | null;
  scoreHelp: ScoreHelp;
  setScoreHelp: (value: ScoreHelp) => void;
  visibleFriendAvatars: Array<{ id: string; name: string; imageUrl: string | null }>;
  subscribed: boolean;
  subscriptionLoading: boolean;
  saved: boolean;
  saving: boolean;
  authenticated: boolean;
  impressionDisabled: boolean;
  impressionLabel: string;
  subscribersCount: number;
  onSubscription: () => void;
  onToggleSaved: () => void;
  onImpression: () => void;
};

function VenueInfoCard({
  detail,
  openingRows,
  statusCopy,
  statusClass,
  compactDescription,
  scoreHelp,
  setScoreHelp,
  visibleFriendAvatars,
  subscribed,
  subscriptionLoading,
  saved,
  saving,
  authenticated,
  impressionDisabled,
  impressionLabel,
  subscribersCount,
  onSubscription,
  onToggleSaved,
  onImpression,
}: VenueInfoCardProps) {
  const score = detail.scores;
  const name = detail.name?.trim() ?? '';

  return (
    <section className="venue-owner-info-card" aria-label="Основная информация о заведении">
      <div className="venue-owner-info-head">
        <VenueAvatar name={detail.name} url={detail.avatar_url} />
        <div className="venue-owner-info-copy">
          {name && <h1>{name}</h1>}
          {compactDescription && <p className="venue-owner-summary">{compactDescription}</p>}
          {detail.address && <span className="venue-owner-address"><HopperIcon name="pin" size={14} /><span>{detail.address}</span></span>}
        </div>
      </div>

      <details className="venue-owner-hours">
        <summary>
          {statusCopy ? <span className={statusClass}><i />{statusCopy}</span> : <span />}
          {openingRows && <b>Часы работы <HopperIcon name="chevron-right" size={14} /></b>}
        </summary>
        {openingRows && <div className="venue-owner-hours-grid">{openingRows.map((row) => <div key={row.day}><span>{row.day}</span><strong>{row.value}</strong></div>)}</div>}
      </details>

      {score && (
        <div className="venue-owner-score-zone">
          <div className="venue-owner-scores">
            <div>
              <span>Hopper Score <button type="button" aria-label="Что такое Hopper Score" onClick={() => setScoreHelp(scoreHelp === 'hopper' ? null : 'hopper')}><HopperIcon name="info" size={14} /></button></span>
              <strong>{score.hopper_score.toLocaleString('ru-RU')}</strong>
            </div>
            <div>
              <span>Live Score <button type="button" aria-label="Что такое Live Score" onClick={() => setScoreHelp(scoreHelp === 'live' ? null : 'live')}><HopperIcon name="info" size={14} /></button></span>
              <strong>{formatLiveScore(score.live_score)}</strong>
            </div>
          </div>
          <button className="venue-owner-metrics-link" type="button" onClick={() => setScoreHelp(scoreHelp === 'metrics' ? null : 'metrics')}>О показателях</button>
          {scoreHelp && <ScoreHelpPanel mode={scoreHelp} detail={detail} />}
        </div>
      )}

      {(visibleFriendAvatars.length > 0 || (score !== null && score.live_unique_guests_7d > 0)) && (
        <div className="venue-owner-social-proof">
          {visibleFriendAvatars.length > 0 && <FriendAvatarsStack friends={visibleFriendAvatars} limit={4} size={26} />}
          {score !== null && score.live_unique_guests_7d > 0 && <span>{score.live_unique_guests_7d.toLocaleString('ru-RU')} гостей были здесь за последние 7 дней</span>}
        </div>
      )}

      <div className="venue-owner-actions">
        <Button className="venue-owner-subscribe" variant={subscribed ? 'secondary' : 'primary'} loading={subscriptionLoading} disabled={!authenticated} onClick={onSubscription}>{subscribed ? 'Вы подписаны' : 'Подписаться'}</Button>
        <Button variant="outline" loading={saving} disabled={!authenticated} leadingIcon={<HopperIcon name="bookmark" size={17} />} onClick={onToggleSaved}>{saved ? 'Сохранено' : 'Сохранить'}</Button>
        <Button className="venue-owner-impression-action" variant="outline" disabled={impressionDisabled} leadingIcon={<HopperIcon name="message" size={17} />} onClick={onImpression}>{impressionLabel}</Button>
      </div>

      <VenueClaimModal venueId={detail.id} />
      {subscribersCount > 0 && <span className="venue-owner-subscriber-count">{subscribersCount.toLocaleString('ru-RU')} подписчиков</span>}
    </section>
  );
}

function EmptyWorkspace({ children }: { children: string }) {
  return <div className="venue-owner-empty"><strong>{children}</strong><span>Здесь появится контент заведения.</span></div>;
}

export function VenueDetailReferenceScreen({
  venueId,
  detail,
  loading,
  authenticated,
  saved,
  saving,
  onToggleSaved,
  onClose,
  onOpenVenue,
  onCreateImpression,
  onEditImpression,
  onScan,
}: VenueDetailCompactScreenProps) {
  const [workspace, setWorkspaceState] = useState<Workspace>(initialVenueWorkspace);
  const [scoreHelp, setScoreHelp] = useState<ScoreHelp>(null);
  const [photos, setPhotos] = useState<VenuePhoto[]>([]);
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('all');
  const [offers, setOffers] = useState<GuestOffer[]>([]);
  const [events, setEvents] = useState<VenueEvent[]>([]);
  const [impressions, setImpressions] = useState<GuestImpression[]>([]);
  const [mineImpressions, setMineImpressions] = useState<GuestImpression[]>([]);
  const [visits, setVisits] = useState<ProfileVisit[]>([]);
  const [related, setRelated] = useState<CityVenue[]>([]);
  const [relatedPlacement, setRelatedPlacement] = useState<RelatedVenuePlacement | null>(null);
  const relatedPlacementButtonRef = useRef<HTMLButtonElement | null>(null);
  const [subscribed, setSubscribed] = useState(detail?.is_subscribed ?? false);
  const [subscribersCount, setSubscribersCount] = useState(detail?.subscribers_count ?? 0);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [activatingOfferId, setActivatingOfferId] = useState<string | null>(null);
  const [lightboxPhoto, setLightboxPhoto] = useState<VenuePhoto | null>(null);

  useEffect(() => {
    setWorkspaceState(initialVenueWorkspace());
    setPhotoFilter('all');
    setLightboxPhoto(null);
    setScoreHelp(null);
    setRelatedPlacement(null);
    relatedPlacementButtonRef.current = null;
  }, [venueId]);

  useEffect(() => {
    setSubscribed(detail?.is_subscribed ?? false);
    setSubscribersCount(detail?.subscribers_count ?? 0);
  }, [detail?.id, detail?.is_subscribed, detail?.subscribers_count]);

  useEffect(() => {
    if (!detail) return undefined;
    const controller = new AbortController();
    const currentPageViewId = pageViewId();

    void fetchAllVenuePhotos(venueId, controller.signal).then(setPhotos).catch(() => setPhotos([]));
    void fetchAllVenueOffers(detail, venueId, controller.signal).then(setOffers).catch(() => setOffers([]));
    void fetchAllVenueEvents(venueId, controller.signal).then(setEvents).catch(() => setEvents([]));
    void fetchAllVenueImpressions(venueId, detail.slug ? [detail.slug] : []).then(setImpressions).catch(() => setImpressions([]));
    void fetchRelatedVenues(detail, venueId, controller.signal).then(setRelated).catch(() => setRelated([]));
    void fetchRelatedVenuePlacement(detail.city.slug || detail.city.id, venueId, currentPageViewId, controller.signal)
      .then(setRelatedPlacement)
      .catch(() => setRelatedPlacement(null));

    if (authenticated) {
      void fetchAllMyVisits().then((items) => setVisits(items.filter((visit) => visit.venue.id === venueId))).catch(() => setVisits([]));
      void fetchAllMineImpressions().then((items) => setMineImpressions(items.filter((item) => item.venue.id === venueId))).catch(() => setMineImpressions([]));
    } else {
      setVisits([]);
      setMineImpressions([]);
    }

    return () => controller.abort();
  }, [authenticated, detail?.city.id, detail?.city.slug, detail?.id, detail?.slug, venueId]);

  useEffect(() => {
    const placement = relatedPlacement;
    const target = relatedPlacementButtonRef.current;
    if (!placement || !target || typeof IntersectionObserver === 'undefined') return undefined;
    let sent = false;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
      if (!visible || sent) return;
      sent = true;
      observer.disconnect();
      void recordRelatedVenuePlacementEvent(placement.delivery_token, 'visible_impression').catch(() => undefined);
    }, { threshold: [0.5] });
    observer.observe(target);
    return () => observer.disconnect();
  }, [relatedPlacement]);

  const filteredPhotos = useMemo(() => {
    if (photoFilter === 'official') return photos.filter((photo) => photo.is_official);
    if (photoFilter === 'guest') return photos.filter((photo) => !photo.is_official);
    return photos;
  }, [photoFilter, photos]);

  const lightboxPhotos = workspace === 'photos' ? filteredPhotos : photos;

  useEffect(() => {
    if (!lightboxPhoto) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxPhoto(null);
        return;
      }

      const currentIndex = lightboxPhotos.findIndex((photo) => photo.id === lightboxPhoto.id);
      if (currentIndex < 0 || lightboxPhotos.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setLightboxPhoto(lightboxPhotos[(currentIndex - 1 + lightboxPhotos.length) % lightboxPhotos.length]);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setLightboxPhoto(lightboxPhotos[(currentIndex + 1) % lightboxPhotos.length]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxPhoto, lightboxPhotos]);

  useEffect(() => {
    if (!lightboxPhoto || workspace !== 'photos') return;
    if (!filteredPhotos.some((photo) => photo.id === lightboxPhoto.id)) {
      setLightboxPhoto(null);
    }
  }, [filteredPhotos, lightboxPhoto, workspace]);

  const openingRows = useMemo(() => normalizeOpeningRows(detail?.opening_hours ?? null), [detail?.opening_hours]);
  const visibleFriendAvatars = useMemo(() => (detail?.friends_who_saved ?? [])
    .filter((friend) => Boolean(friend.avatar_url))
    .map((friend) => ({ id: friend.id, name: friend.display_name?.trim() || '', imageUrl: friend.avatar_url })), [detail?.friends_who_saved]);
  const displayedRelated = useMemo(() => {
    if (!relatedPlacement) return related.slice(0, RELATED_LIMIT);
    const paidVenue = relatedPlacement.venue;
    const organic = related.filter((venue) => venue.id !== paidVenue.id).slice(0, RELATED_LIMIT - 1);
    return [paidVenue, ...organic];
  }, [related, relatedPlacement]);

  if (loading && !detail) return <main className="venue-reference-page venue-owner-page" aria-label="Карточка заведения" />;
  if (!detail) return (
    <main className="venue-reference-page venue-owner-page" aria-label="Карточка заведения">
      <button className="venue-owner-back-fallback" type="button" onClick={onClose}><HopperIcon name="arrow-left" size={19} /> Назад</button>
    </main>
  );

  const venueName = detail.name?.trim() ?? '';
  const compactDescription = shortDescription(detail.description);
  const statusCopy = openingStatusCopy(detail, openingRows);
  const statusClass = detail.opening_status === 'open' ? 'is-open' : detail.opening_status === 'closed' ? 'is-closed' : '';
  const latestVisit = visits[0] ?? null;
  const latestVisitImpression = latestVisit ? mineImpressions.find((item) => item.visit_id === latestVisit.id) ?? null : null;
  const canEditLatest = Boolean(latestVisitImpression && canEditImpression(latestVisitImpression.created_at));
  const impressionDisabled = !authenticated || !latestVisit || Boolean(latestVisitImpression && !canEditLatest);
  const impressionLabel = latestVisitImpression ? (canEditLatest ? 'Изменить впечатление' : 'Впечатление оставлено') : 'Оставить впечатление';

  const handleSubscription = async () => {
    if (!authenticated || subscriptionLoading) return;
    setSubscriptionLoading(true);
    try {
      const response = subscribed ? await unsubscribeVenue(venueId) : await subscribeVenue(venueId);
      setSubscribed(response.subscribed);
      setSubscribersCount(response.subscribers_count);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleImpression = () => {
    if (!latestVisit) return;
    if (latestVisitImpression && canEditLatest) {
      onEditImpression(latestVisitImpression.id);
      return;
    }
    if (!latestVisitImpression) onCreateImpression(latestVisit.id, venueName);
  };

  const activateOffer = async (offer: GuestOffer) => {
    if (offer.my_activation || offer.state !== 'available' || activatingOfferId) return;
    setActivatingOfferId(offer.id);
    try {
      const response = await guestOffersFetch<DataEnvelope<GuestActivation>>(`/api/v1/offers/${encodeURIComponent(offer.id)}/activate`, {
        method: 'POST', body: '{}',
      });
      setOffers((current) => current.map((item) => item.id === offer.id
        ? { ...item, my_activation: { id: response.data.id, status: response.data.status } }
        : item));
    } catch (error) {
      if (!(error instanceof GuestOffersApiError)) throw error;
    } finally {
      setActivatingOfferId(null);
    }
  };

  const selectWorkspace = (nextWorkspace: Workspace) => {
    setLightboxPhoto(null);
    setWorkspaceState(nextWorkspace);
    replaceVenueWorkspaceQuery(nextWorkspace);
  };

  const selectPhotoFilter = (nextFilter: PhotoFilter) => {
    setLightboxPhoto(null);
    setPhotoFilter(nextFilter);
  };

  const infoCardProps: VenueInfoCardProps = {
    detail,
    openingRows,
    statusCopy,
    statusClass,
    compactDescription,
    scoreHelp,
    setScoreHelp,
    visibleFriendAvatars,
    subscribed,
    subscriptionLoading,
    saved,
    saving,
    authenticated,
    impressionDisabled,
    impressionLabel,
    subscribersCount,
    onSubscription: () => void handleSubscription(),
    onToggleSaved,
    onImpression: handleImpression,
  };

  const lightboxPhotoIndex = lightboxPhoto ? lightboxPhotos.findIndex((photo) => photo.id === lightboxPhoto.id) : -1;
  const stepLightboxPhoto = (direction: -1 | 1) => {
    if (lightboxPhotoIndex < 0 || lightboxPhotos.length < 2) return;
    setLightboxPhoto(lightboxPhotos[(lightboxPhotoIndex + direction + lightboxPhotos.length) % lightboxPhotos.length]);
  };

  const offerCards = (items: GuestOffer[]) => (
    <div className="venue-owner-offers">
      {items.map((offer) => {
        const requirement = offerRequirementText(offer);
        const canActivate = !offer.my_activation && offer.state === 'available';
        const isActivated = offer.my_activation?.status === 'active';
        const actionState = canActivate ? 'available' : isActivated ? 'activated' : 'unavailable';
        const actionLabel = canActivate ? 'Активировать' : isActivated ? 'Активировано' : 'Недоступно';
        return (
          <article key={offer.id}>
            <span className={`venue-owner-offer-badge is-${offer.state}`}>{offerStateLabel(offer)}</span>
            <a href={`/offers/${encodeURIComponent(offer.id)}`}>{offer.title}</a>
            <p>{offer.description}</p>
            {requirement && <small>{requirement}</small>}
            <Button
              className={`venue-owner-offer-action is-${actionState}`}
              size="sm"
              variant={canActivate ? 'primary' : 'secondary'}
              loading={canActivate && activatingOfferId === offer.id}
              disabled={!canActivate || Boolean(activatingOfferId && activatingOfferId !== offer.id)}
              onClick={canActivate ? () => void activateOffer(offer) : undefined}
            >
              {actionLabel}
            </Button>
          </article>
        );
      })}
    </div>
  );

  const eventCards = (items: VenueEvent[]) => (
    <div className="venue-owner-events">
      {items.map((event) => (
        <article key={event.id}>
          {event.cover_url ? <img src={event.cover_url} alt="" loading="lazy" /> : <span className="venue-owner-event-placeholder"><HopperIcon name="calendar" size={22} /></span>}
          <div>
            <a href={`/events/${encodeURIComponent(event.id)}`}>{event.title}</a>
            <time>{eventDate(event.starts_at, event.venue.timezone || detail.timezone || detail.city.timezone)}</time>
            {event.description && <p>{event.description}</p>}
          </div>
          <a className="venue-owner-more-link" href={`/events/${encodeURIComponent(event.id)}`}>Подробнее <HopperIcon name="arrow-right" size={14} /></a>
        </article>
      ))}
    </div>
  );

  const photoGrid = (items: VenuePhoto[], preview = false) => (
    <div className={preview ? 'venue-owner-photo-grid is-preview' : 'venue-owner-photo-grid'}>
      {items.map((photo) => <button type="button" key={photo.id} onClick={() => setLightboxPhoto(photo)}><img src={photo.thumbnail_url ?? photo.url} alt="" loading="lazy" /></button>)}
    </div>
  );

  const impressionCards = (items: GuestImpression[]) => (
    <div className="venue-owner-impressions-list">
      {items.map((impression) => (
        <article key={impression.id}>
          <span className="venue-owner-person-placeholder" aria-hidden="true">{(impression.user.display_name?.trim() || 'Г').slice(0, 1).toLocaleUpperCase('ru-RU')}</span>
          <div>
            <div className="venue-owner-impression-top"><strong>{impression.user.display_name ?? 'Гость Хоппера'}</strong><span className={verdictClass(impression.verdict)}><HopperIcon name="check-circle" size={13} /> {IMPRESSION_VERDICT_LABELS[impression.verdict]}</span></div>
            {impression.text && <p>{impression.text}</p>}
            {impression.created_at && <time>{shortDate(impression.created_at)}</time>}
          </div>
        </article>
      ))}
    </div>
  );

  const relatedCards = (items: CityVenue[]) => (
    <div className="venue-owner-related-grid">
      {items.map((venue) => {
        const isServicePlacement = relatedPlacement?.venue.id === venue.id;
        return (
          <button
            type="button"
            key={venue.id}
            ref={isServicePlacement ? relatedPlacementButtonRef : undefined}
            onClick={() => {
              if (isServicePlacement && relatedPlacement) {
                void recordRelatedVenuePlacementEvent(relatedPlacement.delivery_token, 'open').catch(() => undefined);
              }
              onOpenVenue(venue.id);
            }}
          >
            <VenueAvatar name={venue.name} url={venue.avatar_url} className="venue-owner-related-avatar" />
            <span><strong>{venue.name ?? 'Место Хоппера'}</strong>{venue.address && <small>{venue.address}</small>}</span>
          </button>
        );
      })}
    </div>
  );

  const overview = (
    <div className="venue-owner-overview">
      <section className="venue-owner-section venue-owner-about">
        <h2>О заведении</h2>
        {detail.description?.trim() ? <p>{detail.description.trim()}</p> : <p className="venue-owner-muted">Заведение пока не добавило описание.</p>}
      </section>

      <div className="venue-owner-overview-posts">
        <VenuePostsSection venueId={venueId} referenceMode onShowAll={() => selectWorkspace('posts')} />
      </div>

      <section className="venue-owner-section">
        <div className="venue-owner-heading"><h2>Предложения</h2><button type="button" onClick={() => selectWorkspace('offers')}>Все предложения</button></div>
        {offers.length > 0 ? offerCards(offers.slice(0, 2)) : <EmptyWorkspace>Предложений пока нет</EmptyWorkspace>}
      </section>

      <section className="venue-owner-section">
        <div className="venue-owner-heading"><h2>События</h2><button type="button" onClick={() => selectWorkspace('events')}>Все события</button></div>
        {events.length > 0 ? eventCards(events.slice(0, 1)) : <EmptyWorkspace>Событий пока нет</EmptyWorkspace>}
      </section>

      <section className="venue-owner-section">
        <div className="venue-owner-heading"><h2>Фото</h2><button type="button" onClick={() => selectWorkspace('photos')}>Все фото</button></div>
        {photos.length > 0 ? photoGrid(photos.slice(0, 6), true) : <EmptyWorkspace>Фотографий пока нет</EmptyWorkspace>}
      </section>

      <section className="venue-owner-section venue-owner-related-section" aria-labelledby="venue-related-title">
        <div className="venue-owner-heading"><h2 id="venue-related-title">Другие места в городе</h2></div>
        {displayedRelated.length > 0 ? relatedCards(displayedRelated) : <EmptyWorkspace>Других мест пока нет</EmptyWorkspace>}
      </section>
    </div>
  );

  let workspaceContent = overview;
  if (workspace === 'posts') {
    workspaceContent = <div className="venue-owner-workspace-section"><VenuePostsSection venueId={venueId} referenceMode workspaceMode /></div>;
  } else if (workspace === 'offers') {
    workspaceContent = <section className="venue-owner-workspace-section"><div className="venue-owner-heading"><h2>Все предложения</h2></div>{offers.length > 0 ? offerCards(offers) : <EmptyWorkspace>Предложений пока нет</EmptyWorkspace>}</section>;
  } else if (workspace === 'events') {
    workspaceContent = <section className="venue-owner-workspace-section"><div className="venue-owner-heading"><h2>Все события</h2></div>{events.length > 0 ? eventCards(events) : <EmptyWorkspace>Событий пока нет</EmptyWorkspace>}</section>;
  } else if (workspace === 'photos') {
    const emptyPhotoCopy = photoFilter === 'official'
      ? 'Официальных фото пока нет'
      : photoFilter === 'guest'
        ? 'Фото от гостей пока нет'
        : 'Фотографий пока нет';

    workspaceContent = (
      <section className="venue-owner-workspace-section">
        <div className="venue-owner-heading"><h2>Фотогалерея</h2></div>
        <div className="venue-owner-photo-filters" role="tablist" aria-label="Фильтр фотографий">
          {PHOTO_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={photoFilter === filter.id}
              className={photoFilter === filter.id ? 'is-active' : ''}
              onClick={() => selectPhotoFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {filteredPhotos.length > 0 ? photoGrid(filteredPhotos) : <EmptyWorkspace>{emptyPhotoCopy}</EmptyWorkspace>}
      </section>
    );
  } else if (workspace === 'impressions') {
    workspaceContent = <section className="venue-owner-workspace-section"><div className="venue-owner-heading"><h2>Все впечатления</h2></div>{impressions.length > 0 ? impressionCards(impressions) : <EmptyWorkspace>Впечатлений пока нет</EmptyWorkspace>}</section>;
  } else if (workspace === 'visits') {
    workspaceContent = <section className="venue-owner-workspace-section"><div className="venue-owner-heading"><h2>Мои визиты</h2></div>{visits.length > 0 ? <div className="venue-owner-visits-list">{visits.map((visit) => <article key={visit.id}><span>{shortDate(`${visit.local_date}T12:00:00`)}</span><strong>{visit.receipt_count} чек{visit.receipt_count === 1 ? '' : 'а'}</strong></article>)}</div> : <EmptyWorkspace>Подтверждённых визитов пока нет</EmptyWorkspace>}<Button className="venue-owner-workspace-scan" size="sm" variant="primary" leadingIcon={<HopperIcon name="scan" size={16} />} onClick={onScan}>Сканировать чек</Button></section>;
  }

  return (
    <main className="venue-reference-page venue-owner-page" aria-label={`Карточка заведения ${venueName}`}>
      <div className="venue-owner-shell">
        <div className="venue-owner-mobile-topline">
          <button type="button" onClick={onClose} aria-label="Назад"><HopperIcon name="arrow-left" size={20} /></button>
          <span>{detail.city.name}</span>
        </div>

        <section className="venue-owner-banner" aria-label="Баннер заведения">
          {detail.banner_url ? (
            <img src={detail.banner_url} alt="" />
          ) : (
            <div className="venue-owner-banner-placeholder">
              <span className="venue-owner-banner-mark">Х</span>
              <div><strong>Заведение ещё не оформило баннер</strong><p>После активации кабинета здесь появится фирменная обложка.</p></div>
            </div>
          )}
        </section>

        <div className="venue-owner-mobile-info"><VenueInfoCard {...infoCardProps} /></div>

        <nav className="venue-owner-tabs" aria-label="Разделы заведения">
          {WORKSPACE_TABS.map((tab) => (
            <button className={workspace === tab.id ? 'is-active' : ''} type="button" key={tab.id} aria-pressed={workspace === tab.id} onClick={() => selectWorkspace(tab.id)}>{tab.label}</button>
          ))}
        </nav>

        <section className="venue-owner-workspace" aria-live="polite">
          {workspaceContent}
        </section>

        <aside className="venue-owner-rail">
          <VenueInfoCard {...infoCardProps} />

          <section className="venue-owner-rail-card">
            <div className="venue-owner-heading"><div><h2>Впечатления</h2><small>После подтверждённого визита</small></div><button type="button" onClick={() => selectWorkspace('impressions')}>Все</button></div>
            {impressions.length > 0 ? impressionCards(impressions.slice(0, 2)) : <p className="venue-owner-rail-empty">Пока нет впечатлений.</p>}
          </section>

          <section className="venue-owner-rail-card">
            <div className="venue-owner-heading"><h2>Мои визиты</h2><button type="button" onClick={() => selectWorkspace('visits')}>История</button></div>
            {visits.length > 0 ? (
              <div className="venue-owner-visit-summary">
                <HopperIcon name="receipt" size={18} />
                <div><strong>{visits.length} визитов</strong>{detail.my_loyalty && <span>{detail.my_loyalty.local_xp.toLocaleString('ru-RU')} Local XP</span>}</div>
              </div>
            ) : <p className="venue-owner-rail-empty">Подтверждённых визитов пока нет.</p>}
            {detail.my_loyalty?.next_tier && detail.my_loyalty.progress_percent !== null && (
              <div className="venue-owner-loyalty"><i><span style={{ width: `${Math.max(0, Math.min(100, detail.my_loyalty.progress_percent))}%` }} /></i><small>Ещё {(detail.my_loyalty.xp_to_next ?? 0).toLocaleString('ru-RU')} XP до следующего уровня</small></div>
            )}
          </section>
        </aside>
      </div>

      {lightboxPhoto && (
        <div className="venue-owner-lightbox" role="dialog" aria-modal="true" aria-label="Просмотр фото заведения" onClick={() => setLightboxPhoto(null)}>
          <button className="venue-owner-lightbox__close" type="button" aria-label="Закрыть" onClick={() => setLightboxPhoto(null)}>
            <HopperIcon name="close" size={22} />
          </button>
          {lightboxPhotos.length > 1 && (
            <button
              className="venue-owner-lightbox__nav is-prev"
              type="button"
              aria-label="Предыдущее фото"
              onClick={(event) => {
                event.stopPropagation();
                stepLightboxPhoto(-1);
              }}
            >
              ‹
            </button>
          )}
          <img src={lightboxPhoto.url} alt="" onClick={(event) => event.stopPropagation()} />
          {lightboxPhotos.length > 1 && (
            <button
              className="venue-owner-lightbox__nav is-next"
              type="button"
              aria-label="Следующее фото"
              onClick={(event) => {
                event.stopPropagation();
                stepLightboxPhoto(1);
              }}
            >
              ›
            </button>
          )}
        </div>
      )}
    </main>
  );
}
